/**
 * Contratos C1, C3 e C4 da Plataforma (aplicações, link de pagamento e anúncios do
 * Mercado Livre). O C2 (saldo por filial) vive em `catalog/branch-stock.ts`.
 *
 * Tudo aqui é puro e sem I/O: tipos, validação de fronteira (`assert*`) e a regra de
 * reconciliação Anúncios × ERP. Mesma convenção de `api/types.ts`: a validação existe
 * para a resposta HTTP nunca virar dado silenciosamente errado na tela.
 */
import type { PlatformCatalogProduct } from "../api/types.js";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function fail(name: string, reason: string): never {
  throw new Error(`Invalid ${name}: ${reason}`);
}

function text(record: Record<string, unknown>, key: string, name: string): string {
  const value = record[key];
  if (typeof value !== "string" || !value) fail(name, `missing ${key}`);
  return value;
}

function nullableInt(record: Record<string, unknown>, key: string, name: string): number | null {
  const value = record[key];
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isInteger(value)) fail(name, `${key} must be an integer or null`);
  return value;
}

// ---------------------------------------------------------------------------
// C1 — Aplicações (compatibilidade produto × máquina)
// ---------------------------------------------------------------------------
export interface ProductApplication {
  id: string;
  productId: string;
  brand: string;
  model: string;
  /** Faixa de ano opcional (`null` = não informada). */
  yearFrom: number | null;
  yearTo: number | null;
  note: string;
  createdAt: string;
}

export function assertProductApplication(value: unknown): ProductApplication {
  if (!isRecord(value)) fail("ProductApplication", "payload is not an object");
  return {
    id: text(value, "id", "ProductApplication"),
    productId: text(value, "productId", "ProductApplication"),
    brand: text(value, "brand", "ProductApplication"),
    model: text(value, "model", "ProductApplication"),
    yearFrom: nullableInt(value, "yearFrom", "ProductApplication"),
    yearTo: nullableInt(value, "yearTo", "ProductApplication"),
    note: typeof value.note === "string" ? value.note : "",
    createdAt: text(value, "createdAt", "ProductApplication")
  };
}

/** Texto curto da faixa de anos, ou `null` quando nenhuma foi informada. */
export function applicationYears(application: Pick<ProductApplication, "yearFrom" | "yearTo">): string | null {
  const { yearFrom, yearTo } = application;
  if (yearFrom === null && yearTo === null) return null;
  if (yearFrom !== null && yearTo !== null) return yearFrom === yearTo ? String(yearFrom) : `${yearFrom}–${yearTo}`;
  return yearFrom !== null ? `a partir de ${yearFrom}` : `até ${yearTo}`;
}

// ---------------------------------------------------------------------------
// C3 — Link de pagamento
// ---------------------------------------------------------------------------
export const PAYMENT_LINK_STATUSES = ["active", "paid", "expired", "cancelled"] as const;
export type PaymentLinkStatus = (typeof PAYMENT_LINK_STATUSES)[number];

export interface PaymentLink {
  id: string;
  platformOrderId: string;
  amountCents: number;
  /** `simulator` = link fictício (sem credencial do Mercado Pago); nunca apresentado como real. */
  provider: "simulator" | "mercado_pago";
  url: string;
  status: PaymentLinkStatus;
  expiresAt: string;
  createdAt: string;
}

export function assertPaymentLink(value: unknown): PaymentLink {
  if (!isRecord(value)) fail("PaymentLink", "payload is not an object");
  const provider = value.provider;
  if (provider !== "simulator" && provider !== "mercado_pago") fail("PaymentLink", `unknown provider ${String(provider)}`);
  const status = value.status;
  if (!(PAYMENT_LINK_STATUSES as readonly unknown[]).includes(status)) fail("PaymentLink", `unknown status ${String(status)}`);
  if (typeof value.amountCents !== "number" || !Number.isInteger(value.amountCents) || value.amountCents <= 0) fail("PaymentLink", "amountCents must be a positive integer");
  return {
    id: text(value, "id", "PaymentLink"),
    platformOrderId: text(value, "platformOrderId", "PaymentLink"),
    amountCents: value.amountCents,
    provider,
    url: text(value, "url", "PaymentLink"),
    status: status as PaymentLinkStatus,
    expiresAt: text(value, "expiresAt", "PaymentLink"),
    createdAt: text(value, "createdAt", "PaymentLink")
  };
}

/** O link só vale se estiver ativo e dentro do prazo — expirado "na prática" mesmo que o status ainda diga `active`. */
export function isPaymentLinkUsable(link: Pick<PaymentLink, "status" | "expiresAt">, now: Date): boolean {
  return link.status === "active" && Date.parse(link.expiresAt) > now.getTime();
}

// ---------------------------------------------------------------------------
// C4 — Anúncios do Mercado Livre e reconciliação Anúncios × ERP
// ---------------------------------------------------------------------------
export interface MarketplaceListing {
  id: string;
  externalId: string;
  title: string;
  /** SKU do anúncio — chave de reconciliação com o produto do ERP. `null` = anúncio sem SKU. */
  sku: string | null;
  priceCents: number | null;
  stock: number | null;
  status: "active" | "paused" | "closed";
  /** `simulator` = anúncio de demonstração; `mercado_livre` = veio da API do canal. */
  source: "simulator" | "mercado_livre";
  syncedAt: string;
}

export function assertMarketplaceListing(value: unknown): MarketplaceListing {
  if (!isRecord(value)) fail("MarketplaceListing", "payload is not an object");
  if (value.status !== "active" && value.status !== "paused" && value.status !== "closed") fail("MarketplaceListing", `unknown status ${String(value.status)}`);
  if (value.source !== "simulator" && value.source !== "mercado_livre") fail("MarketplaceListing", `unknown source ${String(value.source)}`);
  return {
    id: text(value, "id", "MarketplaceListing"),
    externalId: text(value, "externalId", "MarketplaceListing"),
    title: text(value, "title", "MarketplaceListing"),
    sku: typeof value.sku === "string" && value.sku ? value.sku : null,
    priceCents: nullableInt(value, "priceCents", "MarketplaceListing"),
    stock: nullableInt(value, "stock", "MarketplaceListing"),
    status: value.status,
    source: value.source,
    syncedAt: text(value, "syncedAt", "MarketplaceListing")
  };
}

export type ListingReconciliationStatus = "matched" | "price_diverged" | "stock_diverged" | "both_diverged" | "unlinked";

export const LISTING_RECONCILIATION_LABELS: Record<ListingReconciliationStatus, string> = {
  matched: "Concorda",
  price_diverged: "Preço diverge",
  stock_diverged: "Estoque diverge",
  both_diverged: "Preço e estoque divergem",
  unlinked: "Sem produto no ERP"
};

export interface ListingReconciliationRow {
  listing: MarketplaceListing;
  product: PlatformCatalogProduct | null;
  status: ListingReconciliationStatus;
  /** Diferenças legíveis (vazio quando concorda ou quando não há produto). */
  differences: string[];
}

export interface ListingReconciliation {
  rows: ListingReconciliationRow[];
  /** Produtos do ERP vendáveis que NÃO têm anúncio ativo — o outro lado da reconciliação. */
  productsWithoutListing: PlatformCatalogProduct[];
}

function fold(sku: string): string {
  return sku.trim().toLowerCase();
}

/**
 * Compara anúncios com o catálogo da Plataforma pelo SKU. Regra herdada da comparação com o
 * Bling: campo ausente (`null`) em qualquer lado NÃO é divergência — não há o que comparar, e
 * inventar divergência seria pior que calar. Preço e estoque são comparados com a oferta
 * vendável vigente; sem oferta, o produto não tem preço/estoque a comparar.
 */
export function reconcileListings(listings: MarketplaceListing[], products: PlatformCatalogProduct[]): ListingReconciliation {
  const bySku = new Map(products.map((product) => [fold(product.sku), product]));
  const linkedSkus = new Set<string>();
  const rows = listings.map((listing): ListingReconciliationRow => {
    const product = listing.sku ? (bySku.get(fold(listing.sku)) ?? null) : null;
    if (!product) return { listing, product: null, status: "unlinked", differences: [] };
    if (listing.status === "active") linkedSkus.add(fold(product.sku));
    const offer = product.sellableOffer;
    const differences: string[] = [];
    const priceDiverged = offer !== null && listing.priceCents !== null && listing.priceCents !== offer.priceCents;
    const stockDiverged = offer !== null && listing.stock !== null && listing.stock !== offer.stock;
    if (priceDiverged) differences.push(`preço no anúncio ${listing.priceCents} ≠ ${offer.priceCents} (centavos) na Plataforma`);
    if (stockDiverged) differences.push(`estoque no anúncio ${listing.stock} ≠ ${offer.stock} na Plataforma`);
    const status: ListingReconciliationStatus = priceDiverged && stockDiverged ? "both_diverged" : priceDiverged ? "price_diverged" : stockDiverged ? "stock_diverged" : "matched";
    return { listing, product, status, differences };
  });
  return {
    rows,
    productsWithoutListing: products.filter((product) => product.sellableOffer !== null && !linkedSkus.has(fold(product.sku)))
  };
}
